import { flatten } from 'lodash';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Selector, type SelectorOptionPropsInterface } from 'sonata-react-components';

import { postDataSegment } from '@/resources/audienceTargeting.resources';
import { SelectedList } from '../components/SelectedList';
import { CampaignEventsStyled } from './CampaignEvents.styles';
import { CampaignEventsModal } from './CampaignEventsModal';

type Props = {
  onChange: (settings: Partial<Parameters<typeof postDataSegment>['0']>) => void;
};

type State = {
  deletedItems: string[];
  selectedEvent: SelectorOptionPropsInterface[];
  selectedItems: Record<string, any>[];
  showModal: boolean;
};

function CampaignEvents({ onChange }: Props) {
  const { formatMessage } = useIntl();

  const [{ deletedItems, selectedEvent, selectedItems, showModal }, setState] = useState<State>({
    deletedItems: [],
    selectedEvent: [],
    selectedItems: [],
    showModal: false
  });

  const handleModal = () => {
    setState(prevState => ({
      ...prevState,
      showModal: !showModal
    }));
  };

  const handleModalSave = (items: Record<string, any>[]) => {
    setState(prevState => ({
      ...prevState,
      selectedItems: items
    }));
    onChange({
      campaignIds: items.map(item => item._id),
      type: 'campaign-events'
    });
  };

  const handleDeleteItems = (deletedItem: string) => {
    const updatedDeletedItems = [...deletedItems, deletedItem];
    setState(prevState => ({
      ...prevState,
      deletedItems: updatedDeletedItems
    }));
    const updatedFilteredItemsIds = selectedItems
      .filter(item => !updatedDeletedItems.includes(item._id))
      .map(item => item._id);

    onChange({
      campaignIds: updatedFilteredItemsIds,
      type: 'campaign-events'
    });
  };

  const filteredItems = selectedItems.filter(item => !deletedItems.includes(item._id));

  const defaultSelectedItems = filteredItems.reduce((acc, item) => {
    return {
      ...acc,
      [item._id]: true
    };
  }, {});

  const selectedEvents = [...new Set(flatten(selectedItems.map(item => item.events)))];
  const eventOptions = selectedEvents.map(item => ({
    id: item,
    label: formatMessage({
      defaultMessage: item,
      id: `targeting.audienceTargeting.audienceProfile.campaignEvents.events.cpyStatsName${item}`
    })
  }));

  const handleEventsChange = (items: SelectorOptionPropsInterface[]) => {
    setState(prevState => ({
      ...prevState,
      selectedEvents: items
    }));
    onChange({
      eventTypes: items.map(item => item.id as string),
      type: 'campaign-events'
    });
  };

  return (
    <CampaignEventsStyled className="campaignEvents" showButton={filteredItems.length === 0}>
      <div className="campaignEvents__container">
        {filteredItems.length === 0 ? (
          <ButtonUI
            className="campaignEvents__button"
            id="campaignEvents-button"
            onClick={handleModal}
            size="medium"
            text={formatMessage({
              defaultMessage: 'Select Associated Campaign',
              id: 'targeting.audienceTargeting.audienceProfile.campaignEvents.buttonAssociatedCampaign'
            })}
            type="primary"
          />
        ) : (
          <SelectedList
            items={filteredItems}
            onDeleteItems={handleDeleteItems}
            onModalOpen={handleModal}
            showCount
            title={formatMessage({
              defaultMessage: 'Campaign Selected:',
              id: 'targeting.audienceTargeting.audienceProfile.campaignEvents.list.title'
            })}
          />
        )}
      </div>
      {eventOptions.length > 0 && (
        <Selector
          className="campaignEvents__selector"
          defaultValue={selectedEvent}
          id="campaignEvents-selector"
          label={formatMessage({
            defaultMessage: 'Campaign Events',
            id: 'targeting.audienceTargeting.audienceProfile.campaignEvents.eventSelector'
          })}
          onChange={handleEventsChange}
          options={eventOptions}
          placeholder={formatMessage({
            defaultMessage: 'Select Campaign Events',
            id: 'targeting.audienceTargeting.audienceProfile.campaignEvents.eventPlaceholder'
          })}
          required
          type="multiple"
        />
      )}
      {showModal && (
        <CampaignEventsModal
          defaultSelectedItems={defaultSelectedItems}
          isModalOpened={showModal}
          onModalClose={handleModal}
          onModalSave={handleModalSave}
          theme="light"
        />
      )}
    </CampaignEventsStyled>
  );
}

export default CampaignEvents;
